import api from './api'

export interface MonthAssignment {
  assigned_year: number
  assigned_months: number[]
}

export interface EmployeeAssignment {
  id: number
  employee_id: number
  assigned_year: number
  assigned_months: number[]
  assigned_months_with_names: string[]
  created_at: string
  updated_at: string
  employee: {
    user_id: number
    user_details: {
      first_name: string
      last_name: string
      middle_name?: string
      email: string
    }
  }
}

export interface AssignmentResponse {
  success: boolean
  message: string
  data: EmployeeAssignment
}

export interface AssignmentsResponse {
  success: boolean
  message: string
  data: EmployeeAssignment[]
}

export interface CreateAssignmentPayload {
  assigned_year: number
  assigned_months: number[]
}

export interface UpdateAssignmentPayload {
  assigned_year: number
  assigned_months: number[]
}

export interface EmployeeProgress {
  employee_id: number
  employee: {
    user_id: number
    email: string
    user_image?: string
    user_details: {
      first_name: string
      middle_name?: string
      last_name: string
      full_name: string
    }
  }
  assignment: {
    assigned_year: number
    assigned_months: number[]
    accessible_months: number[]
  } | null
  has_access: boolean
  progress: {
    exam_available: boolean
    exam_taken: boolean
    exam_passed: boolean
    attempts: number
    latest_score: {
      score: number
      total: number
      percentage: number
      date_taken: string
      passed: boolean
    } | null
  }
}

export interface ModuleProgressData {
  module: {
    module_id: number
    name: string
    description?: string
    status: number
    has_exam: boolean
    exam: {
      exam_id: number
      exam_name: string
      passing_rate: number
      time_limit: number
      total_attempts?: number | null
    } | null
  }
  filters: {
    year: number
    month?: number
  }
  summary: {
    total_employees: number
    employees_with_access: number
    exams_taken: number
    exams_passed: number
    completion_rate: number
    pass_rate: number
  }
  employee_progress: EmployeeProgress[]
}

export interface ModuleProgressResponse {
  success: boolean
  message: string
  data: ModuleProgressData
}

export class EmployeeAssignmentService {
  /**
   * Get all employee assignments (Admin only)
   */
  static async getAllAssignments(params?: {
    year?: number
    employee_id?: number
  }): Promise<AssignmentsResponse> {
    try {
      const response = await api.get<AssignmentsResponse>('/employees/assignments', { params })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  /**
   * Assign months to an employee (Admin only)
   */
  static async assignMonths(
    employeeId: number,
    payload: CreateAssignmentPayload
  ): Promise<AssignmentResponse> {
    try {
      const response = await api.post<AssignmentResponse>(
        `/employees/${employeeId}/assign-months`,
        payload
      )
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  /**
   * Update employee month assignments (Admin only)
   */
  static async updateAssignment(
    employeeId: number,
    assignmentId: number,
    payload: UpdateAssignmentPayload
  ): Promise<AssignmentResponse> {
    try {
      const response = await api.put<AssignmentResponse>(
        `/employees/${employeeId}/assign-months/${assignmentId}`,
        payload
      )
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  /**
   * Remove employee month assignments (Admin only)
   */
  static async removeAssignment(employeeId: number, assignmentId: number): Promise<{
    success: boolean
    message: string
  }> {
    try {
      const response = await api.delete(`/employees/${employeeId}/assign-months/${assignmentId}`)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  /**
   * Get authenticated employee's month assignments and available modules
   */
  static async getMyAssignments(): Promise<AssignmentsResponse> {
    try {
      const response = await api.get<AssignmentsResponse>('/my-assignments')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  /**
   * Get employee progress for a specific module (Admin only)
   */
  static async getModuleEmployeeProgress(
    moduleId: number,
    year?: number,
    month?: number
  ): Promise<ModuleProgressResponse> {
    try {
      const params: any = {}
      if (year) params.year = year
      if (month) params.month = month

      const response = await api.get<ModuleProgressResponse>(
        `/employees/module-progress/${moduleId}`,
        { params }
      )
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }
}

export default EmployeeAssignmentService
